import React from "react";
import ReactSelect, { type Props, type StylesConfig } from "react-select";

export type CustomSelectOption = {
  value: string;
  label: string;
};

export interface CustomSelectProps extends Props<CustomSelectOption, false> {
  style?: StylesConfig<CustomSelectOption, false>;
  isBorder?: boolean;
  menuBackgroundColor?: string;
  controlBackgroundColor?: string;
  placeholderColor?: string;
  controlMinHeight?: number;
  controlBorderRadius?: number;
}

const baseStyles: StylesConfig<CustomSelectOption, false> = {
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "var(--color-dark-black)",
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 10,
    cursor: "pointer",
    borderBottom: "1px solid #E5E7EB",
    backgroundColor: state.isSelected ? "#F3F4F6" : state.isFocused ? "#F9FAFB" : "#FFFFFF",
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    boxShadow: "none",
    fontSize: "0.875rem",
    "&:hover": {
      borderColor: state.isFocused ? "var(--color-brand-blue)" : "#E1E1E1",
    },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "var(--color-dark-grey)",
    "&:hover": {
      color: "var(--color-dark-black)",
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: "#9CA3AF",
    padding: 0,
    cursor: "pointer",
    "&:hover": {
      color: "#111827",
    },
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 12,
    paddingRight: 12,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "var(--color-dark-black)",
    textAlign: "left",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "var(--color-dark-grey)",
    textAlign: "left",
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
    color: "rgba(107, 114, 128, 0.8)",
  }),
};

const CustomSelect = React.forwardRef<any, CustomSelectProps>((props, ref) => {
  const {
    style,
    isBorder = true,
    menuBackgroundColor,
    controlBackgroundColor,
    placeholderColor,
    controlMinHeight,
    controlBorderRadius,
    ...rest
  } = props;

  const styles: StylesConfig<CustomSelectOption, false> = {
    ...baseStyles,
    ...(style || {}),
    control: (provided, state) => ({
      ...baseStyles.control?.(provided, state),
      ...(style?.control ? style.control(provided, state) : {}),
      border: isBorder ? "1px solid #E1E1E1" : "none",
      backgroundColor: controlBackgroundColor || "#FFFFFF",
      minHeight: controlMinHeight || 40,
      borderRadius: controlBorderRadius || 8,
    }),
    menu: (provided, state) => ({
      ...baseStyles.menu?.(provided, state),
      ...(style?.menu ? style.menu(provided, state) : {}),
      backgroundColor: menuBackgroundColor || "#FFFFFF",
    }),
    placeholder: (provided, state) => ({
      ...baseStyles.placeholder?.(provided, state),
      ...(style?.placeholder ? style.placeholder(provided, state) : {}),
      color: placeholderColor || "var(--color-dark-grey)",
    }),
    singleValue: (provided, state) => ({
      ...baseStyles.singleValue?.(provided, state),
      ...(style?.singleValue ? style.singleValue(provided, state) : {}),
      color: placeholderColor || "var(--color-dark-black)",
    }),
  };

  return (
    <ReactSelect<CustomSelectOption, false>
      ref={ref}
      {...rest}
      styles={styles}
      menuPortalTarget={document.body}
      menuPosition="fixed"
    />
  );
});

export default CustomSelect;
